import {
  Box,
  Button,
  Flex,
  FlexProps,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SelectProps,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { EntityId, PrintServiceProductEntityHydrated } from "@jackfruit/common"
import React, { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import Badge from "~/components/Badge"
import { useApplicationState } from "~/hooks/useApplicationState"
import { useProductPrices } from "~/hooks/useProductPrices"
import { useSettings } from "~/hooks/useSettings"
import { formatCurrency } from "~/services/Utils"
import { groupProductsByGroup } from "./lineItemUtility"

interface ProductSelectOptionProps extends FlexProps {
  product?: PrintServiceProductEntityHydrated
  isSelected: boolean
}

const ProductSelectOption = ({
  product,
  isSelected,
  ...props
}: ProductSelectOptionProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const { defaultCurrency } = useApplicationState()
  const { productPrices } = useProductPrices(product?.prices ?? [])
  const productPrice = productPrices.find(
    price => price.currency === defaultCurrency
  )
  const showPrice = Boolean(
    productPrice && product?.metaData?.web?.displayPricing
  )

  const showBadge = Boolean(
    product?.metaData?.web?.productListBadge &&
      product?.metaData?.web?.productListBadge.text
  )

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({
        behavior: "auto",
        block: "nearest",
      })
    }
  }, [isSelected])

  return (
    <Flex
      ref={ref}
      justifyContent="space-between"
      fontWeight="semibold"
      color="gray.500"
      flex={1}
      {...props}
    >
      <HStack spacing={4} flex={1} overflow="hidden">
        <Text textOverflow="ellipsis" noOfLines={2}>
          {product?.shortDescription}
        </Text>
        {showBadge && (
          <Badge
            text={product?.metaData?.web?.productListBadge?.text!}
            bg={product?.metaData?.web?.productListBadge?.background}
            color={product?.metaData?.web?.productListBadge?.color}
            fontSize={product?.metaData?.web?.productListBadge?.fontSize}
            border={product?.metaData?.web?.productListBadge?.border}
          />
        )}
      </HStack>
      {showPrice && (
        <Flex ml={3}>
          <Text>
            {formatCurrency(productPrice.currency, productPrice.total)}
          </Text>
        </Flex>
      )}
    </Flex>
  )
}

export interface Props extends SelectProps {
  currentProductId: EntityId
  onProductChange: (id: EntityId) => void
  products: PrintServiceProductEntityHydrated[]
}

const LineItemPreviewProductSelector: React.FC<Props> = ({
  currentProductId,
  onProductChange,
  products,
}) => {
  const { t } = useTranslation()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const { settings } = useSettings()

  const currentProduct = products.find(
    product => product.id === Number(currentProductId)
  )!

  const handleProductChange = useCallback(
    (id: EntityId) => {
      onProductChange(id)
    },
    [onProductChange]
  )

  const hasProductGroupingEnabled = settings?.isExperimentOneEnabled
  const { groupKeys, groups } = groupProductsByGroup(
    products,
    hasProductGroupingEnabled
  )

  return (
    <Popover
      isLazy={true}
      matchWidth={true}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <Flex
          role="button"
          tabIndex={0}
          h="full"
          flex={1}
          textAlign="start"
          alignItems="center"
          px={2}
          borderRadius="md"
          className="select-menu-btn"
          data-testid={`select-menu-btn-${currentProduct?.id}`}
          data-remote-id={currentProduct?.remoteId}
          data-local-id={currentProduct?.id}
          _active={{ cursor: "pointer", bg: "gray.100" }}
          _hover={{ cursor: "pointer", bg: "gray.100" }}
          justifyContent="space-between"
        >
          <Flex flex={0.9}>
            <Text
              fontWeight="bold"
              textOverflow="ellipsis"
              noOfLines={2}
              data-testid={`product-name-${currentProduct?.id}`}
            >
              {currentProduct?.shortDescription}
            </Text>
          </Flex>
          <Button
            size="md"
            colorScheme="primary"
            variant="link"
            _hover={{ textDecoration: "none" }}
          >
            {t(
              "components.cart.line-item.LineItemPreviewProductSelector.MenuButton"
            )}
          </Button>
        </Flex>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody p={0}>
          <Box maxHeight={300} flexDirection="column" overflowY="scroll" p={2}>
            {groupKeys.map((productGroup, groupIndex) => {
              const productsForGroup = groups[productGroup]

              return (
                <React.Fragment key={productGroup}>
                  {groupIndex > 0 && <Box as="hr" my={2} />}
                  {productsForGroup.map(product => {
                    const { id } = product
                    const isSelected = currentProductId === id

                    return (
                      <Box
                        key={id}
                        w="full"
                        as="button"
                        type="button"
                        className="select-menu-btn"
                        aria-label="Change Product"
                        onClick={() => {
                          handleProductChange(id)
                          onClose()
                        }}
                        textAlign="start"
                        display="flex"
                        p={2}
                        data-testid={product.id}
                        data-remote-id={product.remoteId}
                        data-local-id={product.id}
                        borderRadius="md"
                        bg={isSelected ? "gray.200" : "transparent"}
                        _hover={{ bg: "gray.100" }}
                      >
                        <ProductSelectOption
                          product={product}
                          isSelected={isSelected}
                        />
                      </Box>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default LineItemPreviewProductSelector
